import React, { useEffect, useState } from 'react';
import { IListingData } from '@models/listing';
import StaticLayout from '@components/layout/StaticLayout';
import { useLazyQuery } from '@apollo/client';
import { getBusinessPartnerList, getBusinessPartnerLovs } from './queries';
import { getVariables, mapToListingData, toLookups } from './utils';
import { filterSectionsContent, headers } from './content';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import { getFilter, setFilter } from '@utils/filter-utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { useAppSelector } from '@redux/hooks';
import SalesForceBusinessPartnerDrawer from './salesforce-business-partners-drawer/SalesForceBusinessPartnerDrawer';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import { IEnhancedMenuItem } from '@components/table/types';
import { IFilterModel } from '@components/widgets/listing-widget';
import { getError } from '@utils/graph-utils';
import { IAbstractRecord } from '@models';
import { isEqual } from 'lodash';

const PAGE_CONTEXT = 'BusinessPartners';
const FILTER_SESSION_KEY = 'businessPartnersFilter';

const SalesforceBusinessPartnersPage: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();
  const initialFilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          selectedTypes: [],
          selectedRelatedCompany: [],
          businessPartnerStatus: [],
          createdOn: [],
        };
  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialFilterValues,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
  });
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [getBusinessPartnersLazy, { loading: listingLoading }] = useLazyQuery(
    getBusinessPartnerList
  );
  const [getLovsLazy, { loading: lovsLoading }] = useLazyQuery(
    getBusinessPartnerLovs
  );

  const user = useAppSelector((state) => state.user);
  const isAdmin = user.userRoles.includes('Insurance-Admin');
  const actions: IEnhancedMenuItem[] = isAdmin
    ? [
        {
          name: 'newBusinessPartner',
          title: '+ New',
          isPrimary: false,
          onClick: () => {
            setIsDrawerOpen(true);
          },
        },
      ]
    : [];

  const getListingData = (filter = filterValues) => {
    if (!filter) {
      return {};
    }
    const variables = getVariables(filter);
    return getBusinessPartnersLazy({
      variables: variables,
      errorPolicy: 'all',
    })
      .then(async ({ data }) => {
        if (data) {
          const tableData = mapToListingData(data);

          setTableData({
            ...tableData,
            pageNumber: tableData.pageNumber,
            pageSize: tableData.pageSize,
            totalCount: tableData.totalCount,
          });
        }
      })
      .catch((error) => {
        toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
      });
  };

  const getLovs = async (filter = filterValues) => {
    if (!filter) {
      return;
    }
    const { data: lovsData } = await getLovsLazy();
    const lovs = toLookups(lovsData);

    const newFilterSections = filterSectionsContent(lovs, filter.namedFilters);
    setFilterSections(newFilterSections);
    setFilterValues(filter);
  };

  function handlePageChange(
    page: number,
    filterModel = filterValues
  ): Promise<void> {
    return new Promise<void>(() =>
      getListingData({
        ...filterModel,
        pagination: {
          ...filterModel.pagination,
          pageNumber: page,
        },
      })
    );
  }

  useEffect(() => {
    getLovs();
  }, []);

  const renderMainChildren = () => (
    <>
      <ListingWidget
        name="businessPartners"
        title="Business Partners"
        orderByAscendingByDefault
        loading={listingLoading}
        data={tableData}
        tableSettings={{
          headers: headers,
        }}
        actions={actions}
        pageContext={PAGE_CONTEXT}
        onPageChange={(filterModel) => {
          const newFilterModel = {
            ...filterValues,
            ...filterModel,
          };
          setFilterValues(newFilterModel);
          const page = filterModel.pagination.pageNumber;
          return handlePageChange(page, newFilterModel);
        }}
        disableSelection
        usePagination={true}
      />
      {isDrawerOpen && (
        <SalesForceBusinessPartnerDrawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onSuccess={() => getListingData()}
          salesForceBusinessPartnerDetailsInfo={undefined}
        />
      )}
    </>
  );

  const onFilterUpdate = async (filters: IAbstractRecord) => {
    const newFilters = {
      ...filterValues,
      namedFilters: filters,
    };
    if (isEqual(newFilters, filterValues)) {
      return;
    }
    setFilter(filters, FILTER_SESSION_KEY);
    setFilterValues(newFilters);
    getListingData(newFilters);
  };

  const renderFilter = () => {
    return (
      filterSections && (
        <ListingFilterWidget
          name={''}
          filters={filterSections}
          onApplyFilter={(v) => {
            onFilterUpdate(v);
          }}
        />
      )
    );
  };

  return (
    <StaticLayout
      loading={lovsLoading}
      name={'Applications'}
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default SalesforceBusinessPartnersPage;
